import { type ReactElement } from 'react';
import { CHART_TRACK_PX, type ChartTrackSize } from '@vello/tokens';
import { buildRatioSummary, fraction, isFiniteNumber } from '@vello/charts-core';
import { cascade, cx, defaultFormatValue, useMotionState, type ChartToneName } from './kit';
import type { ChartBaseProps } from './line-chart';

// The progress bar of the Claude Design (D549, DS-CHARTS): a pill track (`--chart-track`, 4 / 6 / 8 / 10 px tall by size) with an
// ink fill whose width eases in over 320ms; a LIVE bar adds the FX-06 highlight sweep (1.8s linear) and a pulsing dot (2s). The
// sweep and the pulse are `.fx-*` effects in the design, so `prefers-reduced-motion` stills them (see charts.css); the width
// transition and the entrance are not.

export type { ChartTrackSize };

/** The pixel heights of the sizes, from `@vello/tokens` (`--chart-track-*`). Exposed for tests and for callers that size around a bar. */
export const PROGRESS_TRACK_PX = CHART_TRACK_PX;

export interface ProgressTrackProps {
  /** The share of the whole, 0 … 1 (clamped). */
  readonly fraction: number;
  readonly size?: ChartTrackSize;
  /** `neutral` is the ink fill; the others mark a STATE with the accessible semantic text roles. */
  readonly tone?: ChartToneName;
  /** Play the FX-06 highlight sweep on the fill (a bar that is progressing right now). */
  readonly live?: boolean;
  /** Position in a cascade (a list of bars enters one after the other). */
  readonly index?: number;
  readonly className?: string;
  readonly role?: 'img' | 'progressbar';
  readonly ariaLabel?: string;
  readonly valueNow?: number;
  readonly valueMax?: number;
  /** D566: mark the track as the hovered one (`data-active`), for the outline of the hover state. */
  readonly dataActive?: boolean;
}

/** The bare track + fill. Internal building block of {@link Progress}, `Meter` and `BarList`; exported so a screen can compose its own head. */
export function ProgressTrack({ fraction: share, size = 'md', tone = 'neutral', live = false, index, className, role, ariaLabel, valueNow, valueMax, dataActive }: ProgressTrackProps): ReactElement {
  const f = Math.min(1, Math.max(0, isFiniteNumber(share) ? share : 0));
  const progressbar = role === 'progressbar';
  return (
    <span
      className={cx('vello-bar-list__track', 'vello-progress', className)}
      data-size={size}
      data-live={live ? 'true' : undefined}
      data-active={dataActive === true ? 'true' : undefined}
      {...(role !== undefined ? { role } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      {...(progressbar ? { 'aria-valuemin': 0, 'aria-valuemax': valueMax ?? 100, 'aria-valuenow': valueNow ?? Math.round(f * 100) } : {})}
    >
      <span className="vello-bar-list__fill vello-chart__grow" data-tone={tone} style={{ ...(index !== undefined ? cascade(index) : {}), width: `${f * 100}%` }}>
        {live && <span className="vello-progress__sweep" aria-hidden="true" />}
      </span>
    </span>
  );
}

export interface LiveDotProps {
  readonly className?: string;
}

/** The FX-06 live dot: 9px brand green with a ring that pulses outward every 2s (stilled under reduced motion). */
export function LiveDot({ className }: LiveDotProps): ReactElement {
  return <span className={cx('vello-live-dot', className)} aria-hidden="true" />;
}

export interface ProgressProps extends ChartBaseProps {
  readonly value: number;
  /** The whole the value is measured against (default 100). */
  readonly max?: number;
  /** The accessible name of the bar ("Driver challenge"). Required: a bar with no name says nothing. */
  readonly label: string;
  /** Track height: `xs` 4 (rider flow), `sm` 6 (challenge card), `md` 8 (default), `lg` 10 (city health). */
  readonly size?: ChartTrackSize;
  readonly tone?: ChartToneName;
  /** The bar is progressing right now: FX-06 sweep on the fill. */
  readonly live?: boolean;
}

/**
 * A progress bar in the design's look (A07 / M06 / M13 / FX-06). `role="progressbar"` with the value and the whole in the
 * accessible label; the fill width eases in over 320ms and follows the value when it changes. For a bar that sits under a
 * label and a figure against a limit, use `Meter`.
 */
export function Progress(props: ProgressProps): ReactElement {
  const motion = useMotionState(props.animate);
  const format = props.formatValue ?? defaultFormatValue;
  const max = props.max !== undefined && props.max > 0 ? props.max : 100;
  const value = isFiniteNumber(props.value) ? Math.max(0, props.value) : 0;
  const summary = props.ariaLabel ?? buildRatioSummary({ label: props.label, value, max, format });
  return (
    <span className={cx('vello-chart', 'vello-progress-wrap', props.className)} data-motion={motion}>
      <ProgressTrack
        fraction={fraction(value, max)}
        size={props.size ?? 'md'}
        tone={props.tone ?? 'neutral'}
        live={props.live ?? false}
        role="progressbar"
        ariaLabel={summary}
        valueNow={value}
        valueMax={max}
      />
    </span>
  );
}
